import React, { useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, Truck, LogOut } from 'lucide-react';
import { OrderItemsTable, OrderParserHeader } from './components';
import { useAuth, useToast } from '@/shared/contexts';
import {
  Restaurant,
  OrderItem,
  ParseResponse,
  CreateOrderResponse
} from './types';
import { buildOrderDetailLine } from './orderDetail.util';

const API_URL = import.meta.env.VITE_API_URL || 'https://dev.api.giotheapp.com';

const RestaurantOrderResults: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, accessToken } = useAuth();
  const { notify } = useToast();

  // Get data from location state
  const initialData = location.state || {};
  const result: ParseResponse | null = initialData.result || null;
  const selectedRestaurant: Restaurant | null =
    initialData.selectedRestaurant || null;
  const selectedLocationId: string =
    typeof initialData.locationId === 'string' ? initialData.locationId : '';
  const selectedLocationLabel: string =
    typeof initialData.locationLabel === 'string'
      ? initialData.locationLabel
      : '';
  const tokenFromState = initialData.token || null;

  // URL params
  const urlParams = new URLSearchParams(window.location.search);
  const tokenFromUrl = urlParams.get('token');

  // Local state for editable items
  const [editableItems, setEditableItems] = useState<OrderItem[]>(
    initialData.editableItems || []
  );
  const [selectedUnits, setSelectedUnits] = useState<Record<number, string>>(
    initialData.selectedUnits || {}
  );

  const [creatingOrder, setCreatingOrder] = useState(false);
  const [, setOrderCreated] = useState<CreateOrderResponse | null>(null);
  const [, setError] = useState<string | null>(null);

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  const handleUpdateItem = (
    index: number,
    field: keyof OrderItem,
    value: string | number
  ) => {
    setEditableItems(prev =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleRemoveItem = (index: number) => {
    setEditableItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddItem = () => {
    setEditableItems(prev => [
      ...prev,
      {
        item: '',
        quantity: 1,
        unit: null,
        note: '',
        warning: null,
        is_new: true
      }
    ]);
  };

  const handleUpdateUnit = (index: number, unit: string) => {
    setSelectedUnits(prev => ({ ...prev, [index]: unit }));
  };

  // Empty handlers for vendor (not needed for restaurants)
  const handleAssignVendor = () => {
    // Not needed for restaurants
  };

  const handleCreateOrder = async () => {
    setCreatingOrder(true);
    setError(null);

    try {
      const orderDetails = editableItems.map((item, index) =>
        buildOrderDetailLine(item, selectedUnits[index] ?? item.unit ?? 'unit')
      );

      const response = await axios.post<CreateOrderResponse>(
        `${API_URL}/api/orders/create`,
        {
          new_invoice: true,
          order_number: '',
          order_name: result?.order_name_suggestion || 'New Order',
          delivery_date: new Date(
            Date.now() + 24 * 60 * 60 * 1000
          ).toISOString(),
          total_quantity: editableItems.reduce(
            (sum, item) => sum + item.quantity,
            0
          ),
          estimated_total: 0,
          order_status: 'pending',
          location: selectedLocationLabel || '',
          ...(selectedLocationId
            ? { location_id: selectedLocationId }
            : {}),
          delivery_instructions: result?.delivery_instructions_suggestion || '',
          order_details: orderDetails,
          item_vendor_mappings: []
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken || tokenFromState || tokenFromUrl}`
          }
        }
      );

      setOrderCreated(response.data);
      notify('success', 'Order created successfully!');
      navigate('/order-parser');
    } catch (err: unknown) {
      console.error('Create order error:', err);
      const message = axios.isAxiosError<{ message?: string }>(err)
        ? err.response?.data?.message
        : undefined;
      setError(message || 'Failed to create order');
    } finally {
      setCreatingOrder(false);
    }
  };

  if (!result) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        No data found. Go back and parse an order.
      </div>
    );
  }

  return (
    <div className="light min-h-screen bg-gray-50 flex flex-col">
      <div className="bg-gradient-to-r from-[#006B82] to-[#00809C] text-white px-3 sm:px-6 py-3 flex justify-between items-center">
        <OrderParserHeader
          step="confirm"
          selectedRestaurant={selectedRestaurant}
          onNewOrder={() => navigate('/order-parser')}
        />
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition-colors ml-4"
          title="Logout"
        >
          <LogOut className="w-4 h-4" />
          <span className="hidden sm:inline">Logout</span>
        </button>
      </div>

      <div className="flex-1 py-6 px-4">
        <div className="max-w-5xl mx-auto">
          <section className="flex flex-col bg-white rounded-2xl shadow-xl border border-gray-200/70 overflow-hidden">
            <div className="flex flex-col h-full">
              <OrderItemsTable
                items={editableItems}
                vendors={[]}
                selectedUnits={selectedUnits}
                itemVendorAssignments={{}}
                onUpdateItem={handleUpdateItem}
                onRemoveItem={handleRemoveItem}
                onAddItem={handleAddItem}
                onUpdateUnit={handleUpdateUnit}
                onAssignVendor={handleAssignVendor}
                isSupplier={false}
              />

              {result.delivery_instructions_suggestion && (
                <div className="p-6 bg-gray-50/40 border-t">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Delivery Instructions
                  </h3>
                  <p className="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed bg-white p-4 rounded-xl border border-gray-100">
                    {result.delivery_instructions_suggestion}
                  </p>
                </div>
              )}

              <div className="p-6 border-t bg-white flex justify-end gap-4">
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 font-medium"
                >
                  Start Over
                </button>

                <button
                  onClick={handleCreateOrder}
                  disabled={creatingOrder}
                  className="px-8 py-3 rounded-xl font-semibold text-white bg-[#006B82] hover:bg-[#005A6E] disabled:opacity-50 flex items-center gap-2"
                >
                  {creatingOrder && (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  )}
                  {creatingOrder ? 'Creating...' : 'Create Order'}
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default RestaurantOrderResults;
